import { api } from '@/api/api'
import getWorkspaceApiUrl from '@/api/utils/get-workspace-api-url'
import { QuickSearchKOL } from '@/types/api/search'
import {
  CreateKolsResponse,
  KolsUnlockStatusRequest,
  KolsUnlockStatusResponse,
  createKolsResponseSchema,
  kolsUnlockStatusResponseSchema,
} from '@/types/schema/api/kols'
import {
  ParseLinksResponse,
  parseLinksResponseSchema,
  type ParseLinksRequest,
} from '@/types/schema/kols'

export interface ReportKol
  extends Omit<
    QuickSearchKOL,
    'isVerified' | 'isRadarStar' | 'potential' | 'followerCount'
  > {
  follower_count: number
}
export type ReportKolsResponse =
  | { type: 'success' }
  | { type: 'hidden' }
  | {
      type: 'exist'
      data: ReportKol
    }
  | {
      type: 'error'
    }

export interface ReportKolsRequest {
  url: string
}

interface CreateKolsRequest {
  url: string
  iq_source: {
    platform_user_id: string
    name: string
    follower_count: number
    url: string
  }
}

const kolsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    reportKol: builder.mutation<ReportKolsResponse, ReportKolsRequest>({
      queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/kols`,
          method: 'POST',
          body,
        })

        // kol 已存在
        if (result.error?.status === 409) {
          return {
            data: { type: 'exist', data: result.error.data as ReportKol },
          }
        }

        // kol 被隱藏
        if (result.error?.status === 410) {
          return {
            data: { type: 'hidden' },
          }
        }

        // 預期外的錯誤
        if (result.error) {
          return {
            data: { type: 'error' },
          }
        }

        return {
          data: { type: 'success' },
        }
      },
    }),
    createKol: builder.query<CreateKolsResponse, CreateKolsRequest>({
      query: (body) => {
        return {
          url: '/kols/create',
          method: 'POST',
          body,
        }
      },
      extraOptions: {
        dataSchema: createKolsResponseSchema,
      },
    }),
    parseLinks: builder.mutation<ParseLinksResponse, ParseLinksRequest>({
      query: (body) => {
        return {
          url: '/v3/kols/parse-links',
          method: 'POST',
          body,
        }
      },
      extraOptions: {
        dataSchema: parseLinksResponseSchema,
      },
    }),
    // 批次查詢 kols 有沒有解鎖
    fetchKolsUnlockStatus: builder.query<
      KolsUnlockStatusResponse,
      KolsUnlockStatusRequest
    >({
      query: (body) => {
        return {
          url: getWorkspaceApiUrl('/kols/unlock-status', 'v3'),
          method: 'POST',
          body,
        }
      },
      extraOptions: {
        dataSchema: kolsUnlockStatusResponseSchema,
      },
    }),
  }),
  overrideExisting: false,
})

export const useReportKolsMutation = kolsApi.endpoints.reportKol.useMutation
export const {
  useParseLinksMutation,
  useLazyCreateKolQuery,
  useLazyFetchKolsUnlockStatusQuery,
} = kolsApi
